import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';

import useFilterOptions from './useFilterOptions';

const OPTIONS_BODY = {
  permissions: ['Member'],
  jobRoles: [{ label: 'Technician', value: 'technician' }],
};

// See useUsers.test.js — a probe rather than renderHook, and waitFor rather than
// async act(), which React 16.8 predates.
let hook;

function Probe({ refreshKey = 0 }) {
  hook = useFilterOptions(refreshKey);

  return (
    <div>
      <span data-testid="permissions">
        {hook.permissionOptions.map((o) => o.label).join(',')}
      </span>
      <span data-testid="jobRoles">
        {hook.jobRoleOptions.map((o) => o.label).join(',')}
      </span>
      <span data-testid="accounts">
        {hook.accountOptions.map((o) => o.label).join(',')}
      </span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;

const respondWith = ({
  accounts = [{ label: 'Alpha', value: 1 }],
  permissions = OPTIONS_BODY.permissions,
  total,
} = {}) => {
  global.fetch = jest.fn().mockImplementation((url) => {
    const body = url.startsWith('/management/users/accounts')
      ? { accounts, total }
      : { ...OPTIONS_BODY, permissions };

    return Promise.resolve({ ok: true, json: async () => body });
  });
};

/**
 * Answers the accounts endpoint a page at a time, keyed by the offset asked for.
 *
 * @param {Object} pages offset -> accounts
 * @param {number} total how many match in all
 */
const respondWithPages = (pages, total) => {
  global.fetch = jest.fn().mockImplementation((url) => {
    if (!url.startsWith('/management/users/accounts')) {
      return Promise.resolve({ ok: true, json: async () => OPTIONS_BODY });
    }

    const offset = Number(
      new URL(url, 'http://x').searchParams.get('offset') || 0,
    );

    return Promise.resolve({
      ok: true,
      json: async () => ({ accounts: pages[offset] || [], total }),
    });
  });
};

const accountCalls = () =>
  global.fetch.mock.calls
    .map(([url]) => url)
    .filter((url) => url.startsWith('/management/users/accounts'));

afterEach(() => {
  delete global.fetch;
  hook = undefined;
});

describe('management/Users useFilterOptions', () => {
  it('starts with nothing to offer', () => {
    respondWith();

    render(<Probe />);

    expect(at('permissions')).toEqual('');
    expect(at('accounts')).toEqual('');
  });

  // Permissions and job roles are bounded, so they load once on mount.
  it('loads the bounded lists on mount', async () => {
    respondWith();

    render(<Probe />);

    await waitFor(() => expect(at('permissions')).toEqual('Member'));
    expect(at('jobRoles')).toEqual('Technician');
  });

  it('pairs each role name as its own label and value', async () => {
    respondWith();

    render(<Probe />);

    await waitFor(() =>
      expect(hook.permissionOptions).toEqual([
        { label: 'Member', value: 'Member' },
      ]),
    );
  });

  // The accounts menu opens before anything is typed, so it needs a first page.
  it('loads an initial page of accounts on mount', async () => {
    respondWith();

    render(<Probe />);

    await waitFor(() => expect(at('accounts')).toEqual('Alpha'));
  });

  // The reported gap: UserFilterOptions.accounts offers only accounts that
  // already have a listable user, so creating a user on an account that had none
  // makes it an option for the first time. Until this refetched, the account the
  // new user belongs to was missing from the very menu you would filter by to
  // find them.
  describe('refreshing after a user is created', () => {
    it('re-asks for the accounts', async () => {
      respondWith();
      const { rerender } = render(<Probe refreshKey={0} />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      respondWith({
        accounts: [{ label: 'Alpha', value: 1 }, { label: 'Gamma', value: 3 }],
      });
      rerender(<Probe refreshKey={1} />);

      await waitFor(() => expect(at('accounts')).toEqual('Alpha,Gamma'));
    });

    // The permissions menu offers only the roles a listed user holds (PD-4409),
    // so a create can add one and the menu has to be re-asked.
    it('re-asks for the permissions', async () => {
      respondWith();
      const { rerender } = render(<Probe refreshKey={0} />);
      await waitFor(() => expect(at('permissions')).toEqual('Member'));

      respondWith({ permissions: ['Member', 'Parts Manager'] });
      rerender(<Probe refreshKey={1} />);

      await waitFor(() =>
        expect(at('permissions')).toEqual('Member,Parts Manager'),
      );
    });

    // Otherwise the refresh silently widens a menu the user had narrowed.
    it('re-asks the same question when a search is in force', async () => {
      respondWith();
      const { rerender } = render(<Probe refreshKey={0} />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      hook.searchAccounts('bet');
      await waitFor(() => expect(accountCalls().length).toEqual(2));

      global.fetch.mockClear();
      rerender(<Probe refreshKey={1} />);

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('q=bet');
    });

    // The same guarantee while the search is still debouncing. Reachable
    // without a modal: the bulk import poll bumps the count every 5s, so a
    // refresh can land in the 300ms after a keystroke.
    it('re-asks the same question before the search has settled', async () => {
      respondWith();
      const { rerender } = render(<Probe refreshKey={0} />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch.mockClear();
      hook.searchAccounts('bet');
      rerender(<Probe refreshKey={1} />);

      await waitFor(() => expect(accountCalls().length).toBeGreaterThan(0));
      expect(accountCalls()[0]).toContain('q=bet');
    });
  });

  // PD-4577: the first page alone left the menu looking like the whole list, and
  // a large OEM segment runs to hundreds of accounts.
  describe('paging accounts', () => {
    const PAGES = {
      0: [{ label: 'Alpha', value: 1 }],
      1: [{ label: 'Beta', value: 2 }],
    };

    it('reports the matching total', async () => {
      respondWith({ accounts: [{ label: 'Alpha', value: 1 }], total: 40 });

      render(<Probe />);

      await waitFor(() => expect(hook.accountsTotal).toEqual(40));
    });

    it('knows there is more to load', async () => {
      respondWith({ accounts: [{ label: 'Alpha', value: 1 }], total: 40 });

      render(<Probe />);

      await waitFor(() => expect(hook.hasMoreAccounts).toEqual(true));
    });

    it('knows when the loaded options are all of them', async () => {
      respondWith({ accounts: [{ label: 'Alpha', value: 1 }], total: 1 });

      render(<Probe />);

      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));
      expect(hook.hasMoreAccounts).toEqual(false);
    });

    it('appends the next page rather than replacing the first', async () => {
      respondWithPages(PAGES, 2);
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      hook.loadMoreAccounts();

      await waitFor(() => expect(at('accounts')).toEqual('Alpha,Beta'));
    });

    it('asks for the next page from where the loaded options end', async () => {
      respondWithPages(PAGES, 2);
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch.mockClear();
      hook.loadMoreAccounts();

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('offset=1');
    });

    // Otherwise the scroll listener fires a request per scroll event once the
    // list is exhausted.
    it('does not ask past the end', async () => {
      respondWith({ accounts: [{ label: 'Alpha', value: 1 }], total: 1 });
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch.mockClear();
      hook.loadMoreAccounts();

      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));
      expect(accountCalls()).toEqual([]);
    });

    // A search is a fresh question, so its first page replaces what was loaded
    // rather than adding to it.
    it('starts over when a search narrows the list', async () => {
      respondWithPages(PAGES, 2);
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));
      hook.loadMoreAccounts();
      await waitFor(() => expect(at('accounts')).toEqual('Alpha,Beta'));

      respondWith({ accounts: [{ label: 'Beta', value: 2 }], total: 1 });
      hook.searchAccounts('bet');

      await waitFor(() => expect(at('accounts')).toEqual('Beta'));
      expect(hook.accountsTotal).toEqual(1);
    });

    // The search term has to travel with the offset, or scrolling a narrowed
    // list pages through the unnarrowed one.
    it('keeps the search term when it pages a narrowed list', async () => {
      respondWithPages(PAGES, 2);
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      hook.searchAccounts('a');
      await waitFor(() => expect(accountCalls().length).toEqual(2));

      global.fetch.mockClear();
      hook.loadMoreAccounts();

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('q=a');
      expect(accountCalls()[0]).toContain('offset=1');
    });
  });

  describe('searching accounts', () => {
    it('replaces the options with the matches', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      respondWith({ accounts: [{ label: 'Beta', value: 2 }] });
      hook.searchAccounts('bet');

      await waitFor(() => expect(at('accounts')).toEqual('Beta'));
    });

    it('sends the term to the server', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch.mockClear();
      hook.searchAccounts('bet');

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('q=bet');
    });

    // SearchInput's searchOn="change" fires per keystroke and its documented
    // debounceTimeout is never read by the component, so this is the only thing
    // stopping a request per character.
    it('collapses a burst of keystrokes into one request', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch.mockClear();
      hook.searchAccounts('b');
      hook.searchAccounts('be');
      hook.searchAccounts('bet');

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('q=bet');
    });

    it('empties the options rather than throwing when the search fails', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });
      hook.searchAccounts('bet');

      await waitFor(() => expect(at('accounts')).toEqual(''));
    });
  });

  // A failed option list should not take the table down with it — the rows are
  // the part of the page that matters.
  describe('when the option endpoints fail', () => {
    it('leaves the bounded lists empty without throwing', async () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });

      render(<Probe />);

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());
      expect(at('permissions')).toEqual('');
      expect(at('jobRoles')).toEqual('');
      expect(at('accounts')).toEqual('');
    });
  });
});
